"use client";

import { toast } from "sonner";
import React, { FC, useState } from "react";
import Link from "next/link";
import { useCart } from "@/src/contexts/CartContext";
import { CountdownTimer } from "./CountdownTimer";
import { Select } from "../../ui";
import { AllProductsProps, IProduct } from "@/src/types/product";
import { sortOptions } from "@/src/constant/products/filters";
import { isOfferActive, mapProduct } from "@/src/utils/products/mapProducts";
import { isProductSoldOut } from "@/src/utils/products/stock";
import { buildPriorityRanker } from "@/src/utils/products/priority";
import { SafeImage } from "@/src/components/SafeImage";
import { VariantPickerModal } from "./VariantPickerModal";
import { DeliveryEstimate } from "./DeliveryEstimate";
import { OutletLocation, hasMultipleOutlets } from "./OutletLocation";


export const AllProducts: FC<
  AllProductsProps & { eyebrow?: string; priority?: string[] | null }
> = ({
  products = [],
  outletId = "",
  sortBy = "newest",
  prefix = "",
  limit,
  priority,
  showViewAll = true,
  heading,
  eyebrow,
  multiOutlet,
}) => {
  const { addItem, updateQuantity, removeItem, getItemQuantity } = useCart();
  const [activeSortBy, setActiveSortBy] = useState(sortBy);
  const [pickerProduct, setPickerProduct] = useState<IProduct | null>(null);

  const productList = React.useMemo(() => {
    const mapped = products.map((p) => mapProduct(p, outletId));

    // The chosen sort, applied WITHIN each stock group.
    // "newest" (and "curator") deliberately compare equal: the API already
    // returns products in `createdAt desc` order, so keeping the server order
    // IS newest-first. This used to be a `.reverse()`, which assumed an
    // oldest-first payload and therefore showed the OLDEST products under
    // "Newest First". Sorting properly here isn't possible anyway — mapProduct
    // doesn't carry createdAt.
    const withinGroup =
      activeSortBy === "price-low"
        ? (a: IProduct, b: IProduct) => a.price - b.price
        : activeSortBy === "price-high"
          ? (a: IProduct, b: IProduct) => b.price - a.price
          : () => 0;

    // Sold-out sinks to the bottom under EVERY sort — a buyer's first screen
    // should be things they can actually add. This has to run before `limit`,
    // or a capped rail spends its slots on items nobody can buy (the 8-product
    // grid was leading with a sold-out card).
    //
    // Array.prototype.sort is stable, so the zero comparator above preserves
    // the server's ordering inside each group rather than scrambling it.
    const soldOutRank = (p: IProduct) => (isProductSoldOut(p) ? 1 : 0);

    // Stock first, then the tenant's category order, then the chosen sort.
    // Sold-out outranks priority on purpose: "out of stock last" means last,
    // not "last within its category" — a store opening on Vegetables should not
    // show sold-out vegetables ahead of groceries a buyer can actually add.
    // Priority still orders everything buyable, which is what's seen first.
    // Mirrors the server's $sort in ProductRepository.findAll.
    const rankPriority = buildPriorityRanker(priority);
    const ordered = [...mapped].sort(
      (a, b) =>
        soldOutRank(a) - soldOutRank(b) ||
        (rankPriority ? rankPriority(a) - rankPriority(b) : 0) ||
        withinGroup(a, b)
    );

    return limit ? ordered.slice(0, limit) : ordered;
  }, [products, outletId, activeSortBy, limit, priority]);

  /* Label the branch only for multi-outlet stores — on a single-outlet store the
   * line repeats identically on every card and says nothing.
   *
   * `multiOutlet` is the store-level truth and wins when the caller knows it.
   * Falling back to the rendered list is only for callers that don't pass it,
   * and is deliberately the weaker signal: a paginated page showing 8 of 46 can
   * hold a single outlet's stock while the store has two, which would hide the
   * label on some pages and show it on others. */
  const showOutlet = React.useMemo(
    () => multiOutlet ?? hasMultipleOutlets(productList),
    [multiOutlet, productList]
  );

  // Show "View all" card inside the grid when a limit is active and there are more products
  const showViewAllCard = showViewAll && !!limit && products.length > limit;

  const handleAdd = (product: IProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    // Belt-and-braces: the sold-out card renders no add button at all, but the
    // rule must not live only in JSX — a stale render or a future caller must
    // not be able to push a zero-stock line into the cart.
    if (isProductSoldOut(product)) return;
    if (!product.tenantId) {
      console.warn("[AllProducts] missing tenantId for product", product.id);
      return;
    }
    addItem({
      productId: product.id,
      outletId: product.outletId,
      tenantId: product.tenantId,
      name: product.name,
      price: product.price,
      mrp: product.originalPrice,
      quantity: 1,
      image: product.image,
    }).catch((err) => toast.error((err as Error)?.message || "Couldn't add to cart"));
  };

  const handleQty = (product: IProduct, qty: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (qty <= 0) {
      removeItem(product.id).catch((err) =>
        console.warn("[AllProducts] removeItem failed:", err?.message)
      );
    } else {
      updateQuantity(product.id, qty).catch((err) =>
        console.warn("[AllProducts] updateQuantity failed:", err?.message)
      );
    }
  };

  return (
    <section className="w-full">
      {/* Grid header */}
      <div
        className="mb-6 flex flex-col items-stretch justify-between gap-3 pb-4 sm:flex-row sm:items-end sm:gap-4 sm:pb-5"
        style={{ borderBottom: "1px solid var(--border-soft)" }}
      >
        <div>
          <span
            className="mb-2.5 inline-flex items-center gap-2 text-[12px] font-bold uppercase"
            style={{ color: "var(--coral-deep)", letterSpacing: "0.1em" }}
          >
            <span style={{ width: 18, height: 9, borderRadius: "999px 999px 0 0", background: "var(--coral)", display: "inline-block" }} />
            {eyebrow || "The collection"}
          </span>
          <h2
            className="leading-tight"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              fontSize: "clamp(24px,2.8vw,34px)",
              letterSpacing: "-0.01em",
              color: "var(--techno-black)",
            }}
          >
            {heading || "Handcrafted favourites"}
          </h2>
          <p className="mt-1.5 text-sm" style={{ color: "var(--techno-black-50)" }}>
            Showing {productList.length} of {products.length} products
          </p>
        </div>

        {/* Sort only — "View all" has moved into the grid */}
        <div className="flex w-full items-center justify-end gap-3 sm:w-auto sm:gap-4">
          <Select
            label="Sort"
            value={activeSortBy}
            onChange={(e) => setActiveSortBy(e.target.value)}
            options={sortOptions}
            className="border-[var(--border)] text-[var(--techno-black)] !py-2.5"
          />
        </div>
      </div>


      {/* Product grid */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-5">
        {productList.map((product, index) => {
          const cartQty = getItemQuantity(product.id);
          const hasImage = product.image.startsWith("http");
          const isLowStock = product.stock !== undefined && product.stock > 0 && product.stock <= 5;
          const hasVariants = (product.variantCount ?? 0) >= 2;
          const soldOut = isProductSoldOut(product);

          return (
            <React.Fragment key={product.id}>
              <div
                className="group cursor-pointer"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: "var(--radius-lg)",
                  padding: 12,
                  boxShadow: "var(--shadow-soft)",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-6px)";
                  e.currentTarget.style.boxShadow = "var(--shadow-hover)";
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.boxShadow = "var(--shadow-soft)";
                  e.currentTarget.style.borderColor = "var(--border-soft)";
                }}
              >
                {/* Stretched link: covers the whole card so the card body is one
                    real <a> — prefetched on viewport entry, middle-clickable,
                    keyboard-focusable. Deliberately an overlay rather than
                    wrapping the card: the add/qty/options controls below are
                    <button>s, and nesting them inside an <a> would mean a click
                    on "+" runs the anchor's default action and hard-navigates
                    to the product page. They sit at z-2, above this. */}
                <Link
                  href={`/shop/product/${product.id}?prefix=${prefix}&outletId=${product.outletId}`}
                  aria-label={product.name}
                  className="absolute inset-0"
                  style={{ borderRadius: "var(--radius-lg)", zIndex: 1 }}
                />

                {/* Image area */}
                <div
                  className="relative overflow-hidden"
                  style={{
                    aspectRatio: "1",
                    background: "linear-gradient(135deg, #F4F1EA 0%, #ECE7DC 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "var(--radius)",
                    marginBottom: 14,
                  }}
                >
                  {hasImage ? (
                    <SafeImage
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                      style={soldOut ? { opacity: 0.45 } : undefined}
                    />
                  ) : (
                    <span
                      className="font-extrabold"
                      style={{ fontSize: "clamp(32px,5vw,48px)", color: "rgba(53,73,48,0.12)" }}
                    >
                      {product.name.slice(0, 2).toUpperCase()}
                    </span>
                  )}

                  {/* Sold-out veil — reads at a glance while scanning the grid,
                      so the disabled control below is a confirmation, not a
                      surprise. Sits under the badges/heart (z-2) by default. */}
                  {soldOut && (
                    <span
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-normal"
                      style={{
                        background: "rgba(255,255,255,0.94)",
                        color: "var(--techno-black-70)",
                        boxShadow: "0 2px 10px rgba(40,30,15,0.14)",
                      }}
                    >
                      Sold out
                    </span>
                  )}

                  {/* Badges */}
                  <div className="absolute left-2 top-2 flex flex-col items-start gap-1 sm:left-2.5 sm:top-2.5">
                    {product.pricing?.offer?.isActive && (
                      <span
                        className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-normal text-white sm:text-[10.5px]"
                        style={{ background: "var(--coral)" }}
                      >
                        Offer
                      </span>
                    )}
                    {hasVariants && (
                      <span
                        className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-normal sm:text-[10.5px]"
                        style={{ background: "rgba(255,255,255,0.92)", color: "var(--navy)" }}
                      >
                        {product.variantCount} options
                      </span>
                    )}
                  </div>

                  {/* Favourite (decorative) */}
                  <span
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full transition-colors group-hover:text-[var(--coral)]"
                    style={{
                      background: "rgba(255,255,255,0.92)",
                      backdropFilter: "blur(8px)",
                      color: "var(--techno-black-50)",
                      boxShadow: "0 2px 8px rgba(40,30,15,0.10)",
                      zIndex: 2,
                    }}
                    aria-hidden="true"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 7a3.5 3.5 0 0 1 7 3.5C19 15.5 12 20 12 20z" />
                    </svg>
                  </span>

                  {/* Low stock strip */}
                  {isLowStock && (
                    <div
                      className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-1.5 py-1 text-[11px] font-semibold text-white"
                      style={{ background: "linear-gradient(90deg, rgba(242, 98, 80,0.95), rgba(242, 98, 80,0.85))" }}
                    >
                      <span
                        className="rounded-full"
                        style={{ width: 6, height: 6, background: "#fff", animation: "blip 1s ease-in-out infinite", display: "inline-block" }}
                      />
                      Only {product.stock} left
                    </div>
                  )}
                </div>

                {/* Product info */}
                <div className="flex flex-1 flex-col gap-1.5 px-1 pb-1">
                  {product.category && (
                    <span
                      className="max-w-full self-start truncate rounded-full px-2.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-normal sm:text-[10px]"
                      style={{
                        background: "var(--border-soft)",
                        color: "var(--techno-black-50)",
                      }}
                    >
                      {product.category}
                    </span>
                  )}

                  <h3
                    className="line-clamp-2 leading-snug"
                    style={{
                      fontSize: "clamp(13px, 0.9rem, 14.5px)",
                      fontWeight: 600,
                      letterSpacing: 0,
                      color: "var(--techno-black)",
                      minHeight: 36,
                    }}
                  >
                    {product.name}
                  </h3>

                  {/* Delivery band for the outlet that actually holds THIS product —
                      not the storefront's default. A seller with branches in two
                      cities has a different band per card, and passing the default
                      here would label a Bangalore product with Raipur's "4-7 days".
                      mapProduct already resolves product.outletId per product. */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <DeliveryEstimate outletId={product.outletId} />
                    {showOutlet && (
                      <OutletLocation city={product.outletCity} outletName={product.outletName} />
                    )}
                  </div>

                  {/* Mini countdown — only when offer window is active */}
                  {isOfferActive(product.offerStartTime, product.offerEndTime) && (
                    <div className="relative z-[2] mt-1" onClick={e => e.stopPropagation()}>
                      <CountdownTimer mini endsAt={product.offerEndTime} />
                    </div>
                  )}

                  {/* Price */}
                  <div className="mt-auto flex items-end justify-between gap-2 pt-2">
                    <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
                      <span
                        style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(17px,1.1rem,20px)", color: "var(--techno-black)" }}
                      >
                        ₹{product.price.toLocaleString("en-IN")}
                      </span>
                      {product.originalPrice && (
                        <span
                          className="font-medium"
                          style={{ fontSize: "12.5px", color: "var(--techno-black-50)", textDecoration: "line-through" }}
                        >
                          ₹{product.originalPrice.toLocaleString("en-IN")}
                        </span>
                      )}
                      {product.discountPct && product.discountPct >= 5 && (
                        <span
                          className="rounded-[4px] px-1.5 py-0.5 text-[11px] font-bold"
                          style={{ color: "#F26250", background: "rgba(242, 98, 80,0.1)" }}
                        >
                          {product.discountPct}% off
                        </span>
                      )}
                    </div>

                    {/* Add / qty control. Sold-out wins over every other state:
                        the detail page already refuses to add at zero stock, so
                        the card must not offer a "+" (or a stepper) that the
                        detail page would reject. */}
                    {soldOut ? (
                      <span
                        aria-label="Out of stock"
                        className="relative z-[2] flex h-10 shrink-0 cursor-not-allowed items-center rounded-full px-4 text-[12.5px] font-bold"
                        style={{
                          border: "1.5px solid var(--border)",
                          color: "var(--techno-black-50)",
                          background: "var(--surface-2)",
                        }}
                      >
                        Sold out
                      </span>
                    ) : hasVariants ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPickerProduct(product); }}
                        title="Select options"
                        aria-label="Select options"
                        className="relative z-[2] flex h-10 shrink-0 items-center justify-center gap-1 rounded-full px-4 text-[12.5px] font-bold transition active:scale-95"
                        style={{
                          border: "1.5px solid var(--coral)",
                          background: "var(--surface)",
                          color: "var(--coral)",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = "#F26250";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = "var(--surface)";
                          e.currentTarget.style.color = "#F26250";
                        }}
                      >
                        Options
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    ) : cartQty > 0 ? (
                      <div
                        className="relative z-[2] flex shrink-0 overflow-hidden rounded-full"
                        style={{ height: 40, background: "var(--coral)" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={(e) => handleQty(product, cartQty - 1, e)}
                          className="flex w-9 items-center justify-center text-lg font-light text-white transition hover:bg-black/15"
                        >
                          −
                        </button>
                        <span
                          className="flex min-w-[30px] items-center justify-center text-sm font-bold text-white"
                        >
                          {cartQty}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleQty(product, cartQty + 1, e)}
                          className="flex w-9 items-center justify-center text-lg font-light text-white transition hover:bg-black/15"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleAdd(product, e)}
                        aria-label="Add to cart"
                        className="relative z-[2] flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition active:scale-95"
                        style={{
                          border: "1.5px solid var(--coral)",
                          background: "var(--surface)",
                          color: "var(--coral)",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = "var(--coral)";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = "var(--surface)";
                          e.currentTarget.style.color = "var(--coral)";
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

            </React.Fragment>
          );
        })}
      </div>

      {/* "View all" pill button — below the grid */}
      {showViewAllCard && (
        <div className="mt-6 flex justify-center">
          <Link
            href={`/shop/all-products?prefix=${prefix}`}
            className="group flex items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-6 py-3 text-[13px] font-semibold shadow-sm transition hover:border-[var(--border)] hover:shadow-md"
            style={{ color: "var(--techno-black)" }}
          >
            View all {products.length} products
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              className="transition-transform duration-200 group-hover:translate-y-0.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </Link>
        </div>
      )}

      {/* Inline variant picker — opens when "+" is tapped on a product with variants */}
      {pickerProduct && (
        <VariantPickerModal
          product={pickerProduct}
          onClose={() => setPickerProduct(null)}
        />
      )}
    </section>
  );
};
